/* The accountability log (src/products/mastermind/Log.jsx; docs/mastermind-admin-plan.md 4.3 and 4.6).
   Every colour and font is a --log-* variable, mapped to the mastermind's own tokens on the member's page
   (.mlog--product) and to the shell's in admin (.mlog--shell), so the looks never share a page. Behind
   is a hollow ring with the words in bold; the Accelerator's amber (4.7 to 1 on white) on the member's
   page and ink in admin. The faint grey is never used for small text. */

.mlog {
  --log-bg: var(--shell-bg);
  --log-card: var(--shell-bg);
  --log-ink: var(--shell-ink);
  --log-soft: var(--shell-ink-70);
  --log-rule: var(--shell-ink-12);
  --log-tint: var(--shell-ink-04);
  --log-accent: var(--shell-ink);
  --log-off: var(--shell-ink);
  --log-warn: var(--shell-ink);
  --log-warn-tint: var(--shell-ink-04);
  --log-head-font: var(--shell-font);
  --log-body-font: var(--shell-font);
  max-width: 860px;
  margin: 0 auto;
  color: var(--log-ink);
  font-family: var(--log-body-font);
  font-size: 15px;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.mlog--product {
  --log-bg: var(--acc-bg);
  --log-card: var(--acc-card);
  --log-ink: var(--acc-ink);
  --log-soft: var(--acc-soft);
  --log-rule: var(--acc-rule);
  --log-tint: var(--acc-green-08);
  --log-accent: var(--acc-green);
  --log-off: var(--acc-amber);
  --log-warn: var(--acc-warn);
  --log-warn-tint: var(--acc-warn-06);
  --log-head-font: var(--acc-heading-font);
  --log-body-font: var(--acc-body-font);
}

.mlog p { margin: 0; }
.mlog-visually-hidden {
  position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); border: 0;
}

/* 1. Head */
.mlog-head { display: flex; flex-direction: column; gap: 4px; }
.mlog-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--log-soft); }
.mlog-h1 { margin: 0; font-family: var(--log-head-font); font-size: 32px; line-height: 1.15; font-weight: 700; letter-spacing: -0.01em; }
.mlog-who { font-size: 16px; font-weight: 600; }
.mlog-asof { font-size: 14px; color: var(--log-soft); }
.mlog-tools { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: center; }

.mlog-h2 { margin: 0 0 12px; font-family: var(--log-head-font); font-size: 20px; line-height: 1.25; font-weight: 700; }
.mlog-h3 { margin: 8px 0 4px; font-family: var(--log-head-font); font-size: 17px; font-weight: 700; }

/* the marks: a shape and a word */
.mlog-ico { flex: none; color: var(--log-accent); }
.mlog-mark { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; white-space: nowrap; }
.mlog-mark--off, .mlog-mark--off_track, .mlog-mark--overdue { font-weight: 800; }
.mlog-mark--off .mlog-ico, .mlog-mark--off_track .mlog-ico, .mlog-mark--overdue .mlog-ico { color: var(--log-off); }
.mlog-mark--todo .mlog-ico, .mlog-mark--paused .mlog-ico, .mlog-mark--no_actions .mlog-ico,
.mlog-mark--in_progress .mlog-ico { color: var(--log-soft); }

/* 2. Where you stand */
.mlog-stand {
  background: var(--log-card); border: 1px solid var(--log-rule); border-radius: 14px; padding: 20px 22px;
  display: flex; flex-direction: column; gap: 6px;
}
.mlog-headline { display: flex; align-items: center; gap: 10px; font-family: var(--log-head-font); font-size: 28px; line-height: 1.2; font-weight: 700; }
/* the 3 levels of behind (src/products/mastermind/levels.js): a ring, the words, and a rule down the left that gets
   stronger: amber for A little behind, amber and bold for Behind, the stronger warning, thicker and on a tint, for
   Well behind (never colour alone) */
.mlog-stand--behind-any .mlog-headline .mlog-ico { color: var(--log-off); }
.mlog-stand--behind .mlog-headline, .mlog-stand--well_behind .mlog-headline { font-weight: 800; }
.mlog-stand--well_behind .mlog-headline .mlog-ico { color: var(--log-warn); }
.mlog-stand--well_behind .mlog-headline { color: var(--log-warn); }
.mlog-stand.mlog-stand--behind-any { border-left: 5px solid var(--log-off); }
.mlog-stand.mlog-stand--well_behind { border-left: 8px solid var(--log-warn); background: var(--log-warn-tint); }
.mlog-stand--getting_started .mlog-headline .mlog-ico { color: var(--log-soft); }
/* off track stands out at a glance: a thick rule down the left as well as the ring and the bold words (never colour
   alone) */
.mlog-month.mlog-month--off_track, .mlog-measure.mlog-measure--off { border-left: 5px solid var(--log-off); }
.mlog-stand-line { font-size: 15px; }

/* 3. The 4 measures: 2 by 2 on a laptop, stacked on a phone */
.mlog-measure-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.mlog-measure {
  background: var(--log-card); border: 1px solid var(--log-rule); border-radius: 12px; padding: 16px 18px;
  display: flex; flex-direction: column; gap: 4px; break-inside: avoid;
}
.mlog-measure-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.mlog-measure-h { margin: 0; font-size: 13px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--log-soft); }
.mlog-figure { font-family: var(--log-head-font); font-size: 26px; font-weight: 700; line-height: 1.2; }
.mlog-figure-label { font-family: var(--log-body-font); font-size: 14px; font-weight: 500; color: var(--log-soft); }
.mlog-detail { font-size: 14px; }
.mlog-detail--extra { color: var(--log-soft); }
/* carried into this month and still not done: beside the figure, bold, in the off colour, with its mark */
.mlog-detail--carried { display: flex; align-items: flex-start; gap: 6px; font-weight: 700; }
.mlog-detail--carried .mlog-ico { margin-top: 2px; color: var(--log-off); }
/* Behind on a measure: the detail bold, so not done and missed are read first (Adam, 18 Oct) */
.mlog-measure--off .mlog-detail:not(.mlog-detail--extra) { font-weight: 700; }
.mlog-bar { display: flex; height: 6px; border-radius: 3px; overflow: hidden; background: var(--log-tint); margin: 2px 0 4px; }
.mlog-bar-part--on_time { background: var(--log-accent); }
.mlog-bar-part--late { background: var(--log-accent); opacity: 0.6; }
.mlog-bar-part--part, .mlog-bar-part--not { background: var(--log-off); }

/* 4. The 12 months: they fit at 390 with no sideways scroll */
.mlog-strip { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); gap: 4px; }
.mlog-strip-cell {
  display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 8px 0 6px;
  border: 1px solid var(--log-rule); border-radius: 8px; background: var(--log-card); min-width: 0;
}
.mlog-strip-n { font-size: 12px; font-weight: 600; }
.mlog-strip-cell--off_track .mlog-ico { color: var(--log-off); }
.mlog-strip-cell--off_track .mlog-strip-n { font-weight: 800; }
.mlog-strip-cell--paused .mlog-ico, .mlog-strip-cell--no_actions .mlog-ico,
.mlog-strip-cell--in_progress .mlog-ico, .mlog-strip-cell--to_come .mlog-ico { color: var(--log-soft); }
.mlog-legend { list-style: none; margin: 10px 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 13px; }
.mlog-legend-item { display: inline-flex; align-items: center; gap: 6px; }
.mlog-legend-item:nth-child(2) .mlog-ico { color: var(--log-off); }
.mlog-legend-item:nth-child(n+3) .mlog-ico { color: var(--log-soft); }

/* 5. Month by month: each month stays together, rows never split */
.mlog-months { display: flex; flex-direction: column; gap: 12px; }
.mlog-months > .mlog-h2 { margin-bottom: 0; }
.mlog-copied, .mlog-empty { font-size: 14px; color: var(--log-soft); }
.mlog-month {
  background: var(--log-card); border: 1px solid var(--log-rule); border-radius: 12px; padding: 16px 18px;
  display: flex; flex-direction: column; gap: 4px; break-inside: avoid;
}
.mlog-month-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 2px; }
.mlog-month-h { margin: 0; font-family: var(--log-head-font); font-size: 18px; font-weight: 700; }
.mlog-month-off { font-size: 14px; font-weight: 800; }
.mlog-month-line { font-size: 14px; }
.mlog-month-review { font-weight: 600; }
.mlog-month-none { font-size: 14px; color: var(--log-soft); }
.mlog-actions { list-style: none; margin: 8px 0 0; padding: 0; border-top: 1px solid var(--log-rule); }
.mlog-action {
  display: grid; grid-template-columns: 128px minmax(0, 1fr); gap: 12px; padding: 10px 0;
  border-bottom: 1px solid var(--log-rule); break-inside: avoid;
}
.mlog-action:last-child { border-bottom: none; }
.mlog-action-mark { display: inline-flex; align-items: flex-start; gap: 6px; font-size: 13px; font-weight: 600; }
/* not done, part done (so not done) and carried over still not done stand out: the off colour, bold words and a
   thick rule down the left (never colour alone, never green); done is a filled tick in the accent */
.mlog-action--not_done, .mlog-action--part_done, .mlog-action--carried { box-shadow: inset 4px 0 0 var(--log-off); padding-left: 12px; }
.mlog-action--not_done .mlog-action-mark, .mlog-action--part_done .mlog-action-mark, .mlog-action--carried .mlog-action-mark { font-weight: 800; color: var(--log-off); }
.mlog-action--not_done .mlog-ico, .mlog-action--part_done .mlog-ico, .mlog-action--carried .mlog-ico { color: var(--log-off); }
.mlog-action--not_done .mlog-action-detail, .mlog-action--part_done .mlog-action-detail, .mlog-action--carried .mlog-action-detail { font-weight: 600; }
.mlog-action--done .mlog-action-mark, .mlog-action--done_late .mlog-action-mark { font-weight: 700; }
.mlog-action--set_aside .mlog-ico, .mlog-action--due .mlog-ico { color: var(--log-soft); }
.mlog-action-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.mlog-action-body { font-size: 15px; font-weight: 600; overflow-wrap: anywhere; }
.mlog-action-detail, .mlog-action-extra { font-size: 14px; overflow-wrap: anywhere; }
.mlog-action-extra { color: var(--log-soft); }
.mlog-after { display: flex; flex-direction: column; gap: 12px; }

/* 6 and 7. Wins and how it's worked out */
.mlog-wins, .mlog-rules {
  background: var(--log-card); border: 1px solid var(--log-rule); border-radius: 12px; padding: 18px 20px; break-inside: avoid;
}
.mlog-win-list { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.mlog-win { font-size: 14px; }
.mlog-win-title { font-weight: 600; }
.mlog-win-more summary { cursor: pointer; font-size: 14px; font-weight: 600; min-height: 32px; display: inline-flex; align-items: center; }
.mlog-win-more p { margin: 4px 0 0; }
.mlog-rules-intro, .mlog-rules-foot { font-size: 14px; }
.mlog-rules-foot { margin-top: 8px !important; color: var(--log-soft); }
.mlog-rules-list { margin: 6px 0 0; padding-left: 20px; font-size: 14px; display: flex; flex-direction: column; gap: 4px; }

/* A phone: 1 column, the measures stacked */
@media (max-width: 720px) {
  .mlog { gap: 18px; font-size: 14px; }
  .mlog-h1 { font-size: 26px; }
  .mlog-headline { font-size: 24px; }
  .mlog-stand, .mlog-measure, .mlog-month, .mlog-wins, .mlog-rules { padding: 14px 14px; }
  .mlog-measure-grid { grid-template-columns: minmax(0, 1fr); }
  .mlog-strip { gap: 2px; }
  .mlog-strip-cell { padding: 6px 0 4px; border-radius: 6px; }
  .mlog-strip-n { font-size: 11px; }
  .mlog-action { grid-template-columns: minmax(0, 1fr); gap: 4px; }
}

/* The member's page (look 'product') on screen only (Log.jsx; the Accelerator critique's members 3, 8 and 9, 20 Oct):
   what the refocus card already says is left off the screen and kept in print; a missed session's recording is a
   link; the strip's boxes jump to their months. On a phone: Sessions and Actions as 2 tiles, Goal and Membership as
   1 plain line, this month's card straight after them above the strip, a month that went cleanly folded to its
   heading, and Print at the bottom in About this log. Adam's tab (.mlog--shell), print and the PDF are as before. */
.mlog-facts, .mlog-fold, .mlog-about-tools { display: none; }
@media screen {
  .mlog-printonly { display: none !important; }
  .mlog a.mlog-watch { color: var(--log-accent); font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
  .mlog a.mlog-strip-jump { display: flex; flex-direction: column; align-items: center; gap: 2px; width: 100%; color: inherit; text-decoration: none; }
}
@media print {
  .mlog a.mlog-watch { color: inherit; text-decoration: none; }
  .mlog a.mlog-strip-jump { display: flex; flex-direction: column; align-items: center; gap: 2px; color: inherit; text-decoration: none; }
}
@media screen and (max-width: 720px) {
  .mlog--product .mlog-tools { display: none; }
  .mlog--product .mlog-about-tools { display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: center; margin-top: 14px; }
  .mlog--product .mlog-measure-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .mlog--product .mlog-measure--goal, .mlog--product .mlog-measure--membership { display: none; }
  .mlog--product .mlog-measure .mlog-detail { display: none; }
  .mlog--product .mlog-measure { padding: 12px; }
  .mlog--product .mlog-measure-head { flex-wrap: wrap; gap: 4px 8px; }
  .mlog--product .mlog-figure { font-size: 22px; }
  .mlog--product .mlog-figure-label { display: block; }
  .mlog--product .mlog-facts { display: block; margin-top: 10px; font-size: 14px; }
  .mlog--product .mlog-facts-off .mlog-ico { color: var(--log-off); margin-right: 4px; vertical-align: -2px; }
  /* this month's card above the strip: the months' section steps aside so its cards take their places in the page */
  .mlog--product .mlog-months { display: contents; }
  .mlog--product > .mlog-head { order: 1; }
  .mlog--product > .mlog-lead { order: 2; }
  .mlog--product > .mlog-stand { order: 3; }
  .mlog--product > .mlog-measures { order: 4; }
  .mlog--product .mlog-months > .mlog-month--current { order: 5; }
  .mlog--product > .mlog-strip-wrap { order: 6; }
  .mlog--product .mlog-months > * { order: 7; }
  .mlog--product > .mlog-wins { order: 8; }
  .mlog--product > .mlog-rules { order: 9; }
  .mlog--product .mlog-month-head { justify-content: flex-start; }
  .mlog--product .mlog-month-head .mlog-mark { margin-left: auto; }
  .mlog--product .mlog-fold {
    display: inline-flex; align-items: center; min-height: 36px; padding: 0 2px; border: none; background: none;
    color: var(--log-accent); font: inherit; font-size: 14px; font-weight: 600; text-decoration: underline; text-underline-offset: 3px;
  }
  .mlog--product .mlog-month--folded > :not(.mlog-month-head) { display: none; }
  .mlog--product .mlog-month--folded .mlog-month-head { margin-bottom: 0; }
}

/* The member's page around it */
.mlog-page { padding-bottom: 32px; }
.mlog-hint { font-size: 13px; color: var(--log-soft); }
.mlog-empty-page { margin: 16px 0 0; font-size: 15px; color: var(--acc-soft); }

/* Printing: the document alone, black on white, A4 (Log.jsx writes the @page margin boxes) */
@media print {
  html.mlog-printing body { background: #FFFFFF; }
  html.mlog-printing .ad-side, html.mlog-printing .ad-top, html.mlog-printing .ad-topbar, html.mlog-printing .ad-flash,
  html.mlog-printing .va-bar, html.mlog-printing .viewas-note, html.mlog-printing .mlog-tools, html.mlog-printing .mlog-noprint,
  html.mlog-printing .pp-header, html.mlog-printing .pp-nav, html.mlog-printing .pp-footer, html.mlog-printing .pp-banner,
  html.mlog-printing .amx-noprint { display: none !important; }
  html.mlog-printing .ad-app { display: block; }
  html.mlog-printing .ad-main { padding: 0; }
  .mlog { --log-accent: #000000; --log-off: #000000; --log-ink: #000000; --log-soft: #333333; --log-rule: #BBBBBB; --log-card: #FFFFFF; --log-bg: #FFFFFF; max-width: none; gap: 14px; font-size: 11pt; }
  .mlog-stand, .mlog-measure, .mlog-month, .mlog-wins, .mlog-rules { border-radius: 0; }
  .mlog-month-head, .mlog-h2, .mlog-h3 { break-after: avoid; }
  .mlog-win-more summary { display: none; }
  .mlog-win-more:not([open]) > p { display: block; }
}
